import { useCallback, useState } from 'react'

import {
  recordPayment, postPayment, writeOffInvoiceBalance,
} from '../../services/payment-service'
import { formatAmount } from '../../utils/formatters'
import { useToast } from '../ui'
import { buildPaymentRequest } from './record-payment-helpers'

interface SubmitArgs {
  paymentAmount: number | null
  advanceApplied: number | null
  isForeignCurrency: boolean
  paymentExchangeRate: string
  isVendorBill: boolean
  partyId: string
  partyName: string
  paymentDate: Date
  paymentMethod: string
  paymentRef: string
  selectedBankAccountId: string
  invoiceCurrency: string
  baseCurrency: string
  invoiceId: string
  allocatedAmount: number
  paymentNotes: string
  intermediaryFee: string
  hasSeparateIntermediaryFee: boolean
  destinationFee: string
  hasSeparateDestinationFee: boolean
  feeAccountId: string
  tdsAmount: string
  tdsAccountId: string
  shouldWriteOffRemaining: boolean
  remainingAfterPayment: number
  fxImpact: number
  organizationId: string
  ledgerId: string
  onSuccess: () => void
  onClose: () => void
}

interface SubmitResult {
  isSaving: boolean
  handleSubmit: () => void
}

function validatePayment(
  amt: number, adv: number, isForeignCurrency: boolean, exchangeRate: string,
  addToast: (t: { variant: 'success' | 'error' | 'warning' | 'info'; message: string }) => void,
): boolean {
  if (amt <= 0 && adv <= 0) {
    addToast({ variant: 'error', message: 'Enter a payment amount or apply an advance.' })
    return false
  }
  if (amt > 0 && isForeignCurrency && (!exchangeRate || parseFloat(exchangeRate) <= 0)) {
    addToast({ variant: 'error', message: 'Enter a valid exchange rate.' })
    return false
  }
  return true
}

function buildFxMessage(fxImpact: number, baseCurrency: string): string {
  if (fxImpact === 0) { return '' }
  const direction = fxImpact > 0 ? 'loss' : 'gain'
  return ` (FX ${direction}: ${formatAmount(Math.abs(fxImpact), baseCurrency)})`
}

async function recordAndPostPayment(
  args: SubmitArgs,
): Promise<void> {
  const req = buildPaymentRequest({
    paymentAmount: args.paymentAmount ?? 0,
    advanceApplied: args.advanceApplied ?? 0,
    isVendorBill: args.isVendorBill,
    partyId: args.partyId, partyName: args.partyName || '',
    paymentDate: args.paymentDate, paymentMethod: args.paymentMethod,
    paymentRef: args.paymentRef, selectedBankAccountId: args.selectedBankAccountId,
    invoiceCurrency: args.invoiceCurrency, baseCurrency: args.baseCurrency,
    isForeignCurrency: args.isForeignCurrency, paymentExchangeRate: args.paymentExchangeRate,
    invoiceId: args.invoiceId, allocatedAmount: args.allocatedAmount,
    paymentNotes: args.paymentNotes,
    intermediaryFee: args.intermediaryFee, hasSeparateIntermediaryFee: args.hasSeparateIntermediaryFee,
    destinationFee: args.destinationFee, hasSeparateDestinationFee: args.hasSeparateDestinationFee,
    feeAccountId: args.feeAccountId, tdsAmount: args.tdsAmount, tdsAccountId: args.tdsAccountId,
  })

  const result = await recordPayment(args.organizationId, args.ledgerId, req as Parameters<typeof recordPayment>[2])
  if (!result.data) { throw new Error(result.error?.message ?? 'Failed to record payment') }

  const postResult = await postPayment(args.organizationId, result.data.id)
  if (postResult.error) { throw new Error(postResult.error.message || 'Payment saved as draft but failed to post') }
}

async function handleWriteOff(
  args: SubmitArgs,
  addToast: (t: { variant: 'success' | 'error' | 'warning' | 'info'; message: string }) => void,
): Promise<void> {
  if (!args.shouldWriteOffRemaining || args.remainingAfterPayment <= 0) { return }
  const woResult = await writeOffInvoiceBalance(args.organizationId, args.invoiceId, 'Auto round-off on payment', 'rounding')
  if (woResult.error) {
    addToast({ variant: 'warning', message: `Payment posted, but write-off failed: ${woResult.error.message}` })
  }
}

export function useRecordPaymentSubmit(args: SubmitArgs): SubmitResult {
  const { addToast } = useToast()
  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = useCallback(() => {
    const amt = args.paymentAmount ?? 0
    const adv = args.advanceApplied ?? 0
    if (!validatePayment(amt, adv, args.isForeignCurrency, args.paymentExchangeRate, addToast)) { return }

    setIsSaving(true)
    void recordAndPostPayment(args)
      .then(() => handleWriteOff(args, addToast))
      .then(() => {
        addToast({ variant: 'success', message: `Payment recorded and posted.${buildFxMessage(args.fxImpact, args.baseCurrency)}` })
        args.onSuccess()
        args.onClose()
      })
      .catch((error: unknown) => {
        addToast({ variant: 'error', message: error instanceof Error ? error.message : 'Failed to record payment.' })
      })
      .finally(() => { setIsSaving(false) })
  }, [args, addToast])

  return { isSaving, handleSubmit }
}
